import type { ReactElement } from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib';
import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DatePicker,
    FieldGroup,
    InputError,
    InputGroup,
    InputGroupInput,
    FieldLabel,
    Field,
} from '@crm/shared/ui';

import { usePostponeDealMutation } from './api';
import {
    TPostponeDealFormState,
    POSTPONED_UNTIL_FIELD_NAME,
    POSTPONED_NOTE_FIELD_NAME,
    usePostponeDealDialog,
    usePostponeDealForm,
} from './model';

import type { TDeal } from '@crm/entities/deal';
import type { SubmitHandler } from 'react-hook-form';

interface PostponeDealProps {
    dealIds: readonly TDeal['id'][];
    isOpen?: boolean;
    trigger?: ReactElement | null;
    onOpenChange?: (open: boolean) => void;
    onSubmit?: (data: TPostponeDealFormState) => void;
}

export const PostponeDeal = ({
    dealIds,
    isOpen,
    trigger,
    onOpenChange,
    onSubmit,
}: PostponeDealProps) => {
    const { t } = useTranslation();
    const { postponeDealMutationFn, isPending } = usePostponeDealMutation({ dealIds });
    const { clearErrors, control, handleSubmit, isSubmitting, resetForm } = usePostponeDealForm();
    const { handleDialogOpenChange, isDialogOpen } = usePostponeDealDialog({
        isOpen,
        onOpenChange,
        onClose: resetForm,
    });

    const handleFormSubmit: SubmitHandler<TPostponeDealFormState> = async (data) => {
        await postponeDealMutationFn(data);
        onSubmit?.(data);
        handleDialogOpenChange(false);
    };
    const isBatch = dealIds.length > 1;
    const postponeActionLabel = isBatch
        ? t('crm.postponeDeal.actions.openBatch')
        : t('crm.common.actions.postponeDeal');

    return (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
            {trigger !== null && (
                <DialogTrigger
                    render={trigger ?? <Button type="button">{postponeActionLabel}</Button>}
                />
            )}
            <DialogContent className="max-w-135 p-6" closeBtnStyles="top-4 right-4">
                <form className="contents" onSubmit={handleSubmit(handleFormSubmit)}>
                    <DialogHeader>
                        <DialogTitle>{postponeActionLabel}</DialogTitle>
                        <span className="text-xs leading-4 text-smtr-main-tertiary-100">
                            {isBatch
                                ? t('crm.postponeDeal.dialog.description.batch')
                                : t('crm.postponeDeal.dialog.description.single')}
                        </span>
                    </DialogHeader>

                    <FieldGroup>
                        <Controller
                            name={POSTPONED_UNTIL_FIELD_NAME}
                            control={control}
                            render={({ field, fieldState }) => (
                                <DatePicker
                                    id={field.name}
                                    name={field.name}
                                    label={t('crm.postponeDeal.fields.nextContactDate.label')}
                                    placeholder={t(
                                        'crm.postponeDeal.fields.nextContactDate.placeholder'
                                    )}
                                    value={field.value}
                                    onChange={(value) => {
                                        field.onChange(value);
                                        clearErrors(field.name);
                                    }}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                    disabled={field.disabled || isSubmitting || isPending}
                                    invalid={fieldState.invalid}
                                    errorDisplay="tooltip"
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name={POSTPONED_NOTE_FIELD_NAME}
                            control={control}
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel htmlFor={field.name}>
                                        {t('crm.postponeDeal.fields.note.label')}
                                    </FieldLabel>
                                    <InputGroup>
                                        <InputGroupInput
                                            id={field.name}
                                            aria-invalid={fieldState.invalid}
                                            aria-describedby={
                                                fieldState.error ? `${field.name}-error` : undefined
                                            }
                                            className={cn()}
                                            {...field}
                                            onChange={(event) =>
                                                field.onChange(event.currentTarget.value)
                                            }
                                            placeholder={t(
                                                'crm.postponeDeal.fields.note.placeholder'
                                            )}
                                            disabled={field.disabled || isSubmitting || isPending}
                                        />
                                        {fieldState.invalid && (
                                            <InputError
                                                errorMessage={fieldState.error?.message}
                                                iconClassName="size-3.5"
                                            />
                                        )}
                                    </InputGroup>
                                </Field>
                            )}
                        />
                    </FieldGroup>

                    <DialogFooter className="border-t-0 bg-white">
                        <DialogClose
                            render={
                                <Button
                                    disabled={isSubmitting || isPending}
                                    type="button"
                                    variant="outline"
                                >
                                    {t('button.cancel.text')}
                                </Button>
                            }
                        />
                        <Button disabled={isSubmitting || isPending} type="submit">
                            {isBatch
                                ? t('crm.postponeDeal.actions.submit.batch')
                                : t('crm.postponeDeal.actions.submit.single')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
